import React, { useState, useEffect } from 'react';
import ContextMenu from 'Components/UI/ContextMenu/index';
import PropTypes from 'prop-types';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import {
  createActionItem,
  appendToNoteForMeeting,
} from 'Components/UI/ContextMenu/utils';
import useUserContext from 'Context/UserContext';
import { useContextMenuContext } from 'Components/UI/ContextMenu/ContextMenuProvider';
import { formatDate, fullDateNoPad } from 'Consts/DateFormats';

const MessagingContextMenu = ({ children }) => {
  // Grab data we need to form config. Grab data from a context provider so we don't send requests for each message
  const contextMenuContext = useContextMenuContext();
  const { householdResource } = useSimpleHousehold();
  const [configOptions, setConfigOptions] = useState([]);

  const getPlannerOption = (actionItemCollection, nextScheduledMeetingResource) => {
    return ({
      label: 'Create Planner Task',
      test: 'messages-context-planner-option',
      onClick: (e, selection, handleClose) => {
        createActionItem(
          selection,
          false,
          null,
          householdResource,
          actionItemCollection,
          nextScheduledMeetingResource,
        );
        handleClose();
      },
    });
  };

  const getAssociatePlannerOption = (aiCollection, nextScheduledMeetingResource) => {
    if (!aiCollection) {
      return null;
    }
    const associatePlanners = aiCollection
      .meta()
      .field('assignedTo')
      .options()
      .list()
      .filter(v => v.attr('group') === 'AssociatePlanner');
    if (associatePlanners.length === 0) {
      return null;
    }
    const newAssociatePlannerOption = {
      label: 'Create Associate Planner Task',
      test: 'messages-context-ap-option',
      submenu: { test: 'messages-context-ap-submenu', options: [] },
    };
    associatePlanners
      .forEach((opt) => {
        const apOption = {
          label: opt.attr('prompt'),
          onClick: (e, selection, handleClose) => {
            createActionItem(selection, false, opt.attr('id'), householdResource, aiCollection, nextScheduledMeetingResource);
            handleClose();
          },
        };
        newAssociatePlannerOption.submenu.options.push(apOption);
      });

    return newAssociatePlannerOption;
  };

  const getAppendNotesOption = (meetingCollection, nextScheduledMeetingResource) => {
    if (!meetingCollection) {
      return null;
    }
    const newAppendNotesOption = {
      label: 'Append to Current Notes',
      test: 'messages-context-append-option',
      submenu: { test: 'messages-context-append-submenu', options: [] },
    };
    if (nextScheduledMeetingResource?.id()) {
      // Label the next meeting as '{Date} Next Meeting'
      const dateString = nextScheduledMeetingResource?.attr?.('startTime') ? formatDate(nextScheduledMeetingResource.attr('startTime'), fullDateNoPad) : '';
      newAppendNotesOption.submenu.options.push({
        label: `${dateString} ${nextScheduledMeetingResource?.attr?.('title')}`,
        test: 'messages-context-append-submenu-option',
        onClick: (e, selection, handleClose) => {
          appendToNoteForMeeting(selection, nextScheduledMeetingResource);
          handleClose();
        },
      });
    }
    meetingCollection
      .resources()
      .list()
      .forEach(opt => {
        // Skip the next meeting since its already been added
        if (opt.id() === nextScheduledMeetingResource?.id()) {
          return;
        }
        const dateString = opt?.attr?.('startTime') ? formatDate(opt.attr('startTime'), fullDateNoPad) : '';
        newAppendNotesOption.submenu.options.push({
          label: `${dateString} ${opt.attr('title')}`,
          test: 'messages-context-append-submenu-option',
          onClick: (e, selection, handleClose) => {
            appendToNoteForMeeting(selection, opt);
            handleClose();
          },
        });
      });

    return newAppendNotesOption;
  };

  useEffect(() => {
    if (contextMenuContext) {
      const newConfigOptions = [];
      const plannerOption = getPlannerOption(contextMenuContext?.actionItemCollection, contextMenuContext?.nextScheduledMeetingResource);
      if (plannerOption) {
        newConfigOptions.push(plannerOption);
      }

      const associatePlannerOption = getAssociatePlannerOption(contextMenuContext?.actionItemCollection, contextMenuContext?.nextScheduledMeetingResource);
      if (associatePlannerOption) {
        newConfigOptions.push(associatePlannerOption);
      }

      const appendNotesOption = getAppendNotesOption(contextMenuContext?.meetingCollection, contextMenuContext?.nextScheduledMeetingResource);
      if (appendNotesOption) {
        newConfigOptions.push(appendNotesOption);
      }

      setConfigOptions(newConfigOptions);
    }
  }, [contextMenuContext]);

  const config = {
    options: configOptions,
  };

  return (
    <ContextMenu config={config} test={'messages-context-menu'}>
      {children}
    </ContextMenu>
  );
};

MessagingContextMenu.defaultProps = {
};

MessagingContextMenu.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

const MessagingContextMenuWrapper = ({ children, ...props }) => {
  const { isAgent, hasSuperUserRole } = useUserContext();

  // Just pass through the children content if user is a client
  if (!isAgent && !hasSuperUserRole) {
    return children;
  }

  return (
    <MessagingContextMenu {...props}>
      {children}
    </MessagingContextMenu>
  );
};

export default MessagingContextMenuWrapper;
